import React, { useState, useEffect, useCallback } from 'react';
import { apiService, AdminUser, UserMemory } from '../services/api';

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

const FEEDBACK_BADGE: Record<string, string> = {
  correct:   'bg-green-100 text-green-700',
  incorrect: 'bg-red-100   text-red-700',
  mixed:     'bg-yellow-100 text-yellow-700',
};

// ─── Edit User Modal ───────────────────────────────────────────────────────────

interface EditModalProps {
  user: AdminUser;
  onClose: () => void;
  onSaved: (updated: AdminUser) => void;
}

function EditUserModal({ user, onClose, onSaved }: EditModalProps) {
  const [form, setForm] = useState({
    name:        user.name  ?? '',
    email:       user.email ?? '',
    institution: user.institution ?? '',
    role:        user.role,
    is_active:   user.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await apiService.adminUpdateUser(user.id, {
        name:        form.name        || undefined,
        email:       form.email       || undefined,
        institution: form.institution || undefined,
        role:        form.role,
        is_active:   form.is_active,
      });
      onSaved(res.user);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Edit User — <span className="font-mono text-purple-700">{user.username}</span>
        </h2>

        {error && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Display Name</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
            <input
              type="email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Institution</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={form.institution}
              onChange={e => setForm(f => ({ ...f, institution: e.target.value }))}
              placeholder="Optional"
            />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value as 'user' | 'admin' }))}
              >
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={form.is_active ? 'active' : 'inactive'}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.value === 'active' }))}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Memory Panel ─────────────────────────────────────────────────────────────

interface MemoryPanelProps {
  user: AdminUser;
  onCountChange: (userId: number, newCount: number) => void;
}

function MemoryPanel({ user, onCountChange }: MemoryPanelProps) {
  const [memories, setMemories]   = useState<UserMemory[] | null>(null);
  const [loading,  setLoading]    = useState(false);
  const [clearing, setClearing]   = useState(false);
  const [error,    setError]      = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.adminGetUserMemories(user.id);
      setMemories(data);
    } catch {
      setError('Failed to load memories');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const deleteOne = async (memoryId: number) => {
    try {
      await apiService.adminDeleteMemory(memoryId);
      setMemories(prev => {
        const next = (prev ?? []).filter(m => m.id !== memoryId);
        onCountChange(user.id, next.length);
        return next;
      });
    } catch {
      setError('Failed to delete memory');
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete all ${memories?.length ?? 0} memories for "${user.username}"? This cannot be undone.`)) return;
    setClearing(true);
    setError('');
    try {
      await apiService.adminDeleteAllUserMemories(user.id);
      setMemories([]);
      onCountChange(user.id, 0);
    } catch {
      setError('Failed to clear memories');
    } finally {
      setClearing(false);
    }
  };

  if (loading) return <div className="py-6 text-center text-sm text-gray-400">Loading memories…</div>;
  if (error)   return <div className="py-4 text-sm text-red-600">{error}</div>;
  if (!memories) return null;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">
          {memories.length} {memories.length === 1 ? 'memory' : 'memories'}
        </span>
        {memories.length > 0 && (
          <button
            onClick={clearAll}
            disabled={clearing}
            className="text-xs px-3 py-1 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
          >
            {clearing ? 'Clearing…' : 'Clear All Memories'}
          </button>
        )}
      </div>

      {memories.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No memories stored for this user.</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {memories.map(m => (
            <div
              key={m.id}
              className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-700 leading-relaxed break-words">{m.content}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs text-gray-400">{fmtDate(m.created_at)}</span>
                  {m.feedback && (
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${FEEDBACK_BADGE[m.feedback]}`}>
                      {m.feedback}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => deleteOne(m.id)}
                title="Delete this memory"
                className="flex-shrink-0 text-gray-400 hover:text-red-600 transition-colors mt-0.5"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function UserManagement() {
  const [users,       setUsers]       = useState<AdminUser[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [editTarget,  setEditTarget]  = useState<AdminUser | null>(null);
  const [expandedId,  setExpandedId]  = useState<number | null>(null);
  const [deletingId,  setDeletingId]  = useState<number | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.adminGetUsers();
      setUsers(data);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const handleEditSaved = (updated: AdminUser) => {
    setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
    setEditTarget(null);
  };

  const handleDelete = async (user: AdminUser) => {
    if (!window.confirm(`Delete user "${user.username}"? All their memories will also be deleted.`)) return;
    setDeletingId(user.id);
    try {
      await apiService.adminDeleteUser(user.id);
      setUsers(prev => prev.filter(u => u.id !== user.id));
      if (expandedId === user.id) setExpandedId(null);
    } catch (err: any) {
      alert(err.response?.data?.error ?? 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  const handleMemoryCountChange = (userId: number, newCount: number) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, memory_count: newCount } : u));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="text-red-600">{error}</p>
        <button onClick={loadUsers} className="text-sm text-purple-600 hover:underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-sm text-gray-500 mt-1">{users.length} registered {users.length === 1 ? 'user' : 'users'}</p>
        </div>
        <button
          onClick={loadUsers}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">User</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Role</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Memories</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Last Login</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(user => (
              <React.Fragment key={user.id}>
                {/* User row */}
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 ${user.role === 'admin' ? 'bg-purple-600' : 'bg-blue-500'}`}>
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{user.username}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                        {user.name && <div className="text-xs text-gray-400">{user.name}</div>}
                        {user.institution && <div className="text-xs text-gray-400 italic">{user.institution}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setExpandedId(expandedId === user.id ? null : user.id)}
                      className="flex items-center gap-1.5 text-gray-700 hover:text-purple-700 transition-colors"
                    >
                      <span className="font-medium">{user.memory_count}</span>
                      <svg
                        className={`w-3.5 h-3.5 transition-transform ${expandedId === user.id ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(user.last_login_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setEditTarget(user)}
                        title="Edit user"
                        className="p-1.5 rounded-lg text-gray-500 hover:text-purple-700 hover:bg-purple-50 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        disabled={deletingId === user.id}
                        title="Delete user"
                        className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expanded memory panel */}
                {expandedId === user.id && (
                  <tr>
                    <td colSpan={6} className="px-6 pb-4 bg-blue-50/40">
                      <MemoryPanel user={user} onCountChange={handleMemoryCountChange} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-gray-400 text-sm">
                  No registered users yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editTarget && (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={handleEditSaved}
        />
      )}
    </div>
  );
}
